/**
 * Resolve once window.Razorpay is constructible, injecting checkout.js if it
 * is not already on the page.
 *
 * checkout.js is loaded after the first painted frame (see the injector in
 * public/index.html), and before 2026-10-10 it could come back undefined on
 * flaky mobile Safari even as a synchronous tag (real incident, iOS Safari
 * 2026-07-16). Every `new window.Razorpay(options)` call site MUST await this
 * first or it throws on exactly the users whose network already failed once.
 *
 * On failure (load error or timeout) the tag is removed, so the NEXT attempt
 * injects a fresh one instead of waiting on a corpse whose load/error events
 * already fired and will never fire again.
 *
 * SubscriptionSection.tsx predates this util and still carries its own
 * similar local copy.
 */
export const ensureRazorpay = (timeoutMs = 8000): Promise<void> =>
  new Promise((resolve, reject) => {
    if ((window as any).Razorpay) return resolve();
    const SRC = "https://checkout.razorpay.com/v1/checkout.js";

    let s = document.querySelector(
      `script[src="${SRC}"]`,
    ) as HTMLScriptElement | null;

    const fail = (err: Error) => {
      window.clearTimeout(t);
      // A failed tag never fires events again; clear it so a retry works.
      if (s && !(window as any).Razorpay) s.remove();
      reject(err);
    };
    const t = window.setTimeout(() => {
      // The load event can be missed when the tag predates our listeners
      // (e.g. it finished between querySelector and addEventListener), so
      // re-check the constructor before declaring failure.
      if ((window as any).Razorpay) return resolve();
      fail(new Error("razorpay_script_timeout"));
    }, timeoutMs);
    const done = () => {
      window.clearTimeout(t);
      resolve();
    };

    if (!s) {
      s = document.createElement("script");
      s.src = SRC;
      s.async = true;
      document.body.appendChild(s);
    }
    s.addEventListener("load", done);
    s.addEventListener("error", () => fail(new Error("razorpay_script_failed")));
  });
